import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchOrganizations,
  createOrganizationThunk,
  updateOrganizationThunk,
  deleteOrganizationThunk,
  selectOrganizations,
  selectOrganizationsLoading,
  selectOrganizationsError,
  selectCurrentOrganization,
  setCurrentOrganizationId,
} from "../store/organizationSlice";
import { fetchUserProfile } from "@/modules/auth/store/authSlice";
import {
  CreateOrganizationDto,
  UpdateOrganizationDto,
} from "../infrastructure/organization-service";
import { Organization } from "../entities/Organization";
import { AppDispatch } from "@/store";

export function useOrganizationsService() {
  const dispatch = useDispatch<AppDispatch>();

  const organizations = useSelector(selectOrganizations);
  const isLoading = useSelector(selectOrganizationsLoading);
  const error = useSelector(selectOrganizationsError);
  const currentOrganization = useSelector(selectCurrentOrganization);

  const loadOrganizations = useCallback(
    () => dispatch(fetchOrganizations()).unwrap(),
    [dispatch]
  );

  const createOrganization = useCallback(
    async (dto: CreateOrganizationDto) => {
      const result = await dispatch(createOrganizationThunk(dto)).unwrap();
      await dispatch(fetchUserProfile()).unwrap();
      return result;
    },
    [dispatch]
  );

  const updateOrganization = useCallback(
    (id: string, dto: UpdateOrganizationDto) =>
      dispatch(updateOrganizationThunk({ id, dto })).unwrap(),
    [dispatch]
  );

  const deleteOrganization = useCallback(
    (id: string) => dispatch(deleteOrganizationThunk(id)).unwrap(),
    [dispatch]
  );

  const selectOrganization = useCallback(
    (org: Organization) => {
      dispatch(setCurrentOrganizationId(org.id));
    },
    [dispatch]
  );

  const setOrganizationById = useCallback(
    (id: string) => {
      dispatch(setCurrentOrganizationId(id))
    },
    [dispatch],
  )

  return {
    organizations,
    isLoading,
    error,
    currentOrganization,
    loadOrganizations,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    selectOrganization,
    setOrganizationById,
  }
}

export function useCurrentOrganization(): Organization | null {
  return useSelector(selectCurrentOrganization);
}
